<template>
  <div class="container-fluid py-4">
    <div class="row mb-4">
      <!-- Main Content -->
      <div class="col-12">
        <!-- Topbar -->
        <div
          class="topbar d-flex justify-content-between align-items-center mb-4"
        >
          <h1 class="h3 mb-0 text-gray-800">Timesheets</h1>
          <div class="d-flex align-items-center">
            <div class="input-group me-3" style="max-width: 300px">
              <input
                type="text"
                class="form-control"
                placeholder="Search timesheets..."
                v-model="searchQuery"
              />
              <button class="btn btn-outline-secondary" type="button">
                <i class="bi bi-search"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="mt-3">Loading timesheet data...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="alert alert-danger" role="alert">
          <h4 class="alert-heading">Error Loading Data</h4>
          <p>{{ error }}</p>
          <button class="btn btn-primary" @click="refetch">Try Again</button>
        </div>

        <!-- Content -->
        <div v-else>
          <!-- Stats Cards -->
          <div class="row mb-4">
            <div class="col-xl-3 col-md-6">
              <div class="card stats-card total h-100">
                <div class="card-body">
                  <div class="row align-items-center">
                    <div class="col">
                      <div class="text-xs fw-bold text-uppercase mb-1">
                        Total Entries
                      </div>
                      <div class="h5 mb-0 fw-bold text-gray-800">
                        {{ clockInOuts.length }}
                      </div>
                    </div>
                    <div class="col-auto">
                      <i class="bi bi-list-check fa-2x text-gray-300"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-xl-3 col-md-6">
              <div class="card stats-card approved h-100">
                <div class="card-body">
                  <div class="row align-items-center">
                    <div class="col">
                      <div class="text-xs fw-bold text-uppercase mb-1">
                        Approved
                      </div>
                      <div class="h5 mb-0 fw-bold text-success">
                        {{ approvedCount }}
                      </div>
                    </div>
                    <div class="col-auto">
                      <i class="bi bi-check-circle fa-2x text-gray-300"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-xl-3 col-md-6">
              <div class="card stats-card pending h-100">
                <div class="card-body">
                  <div class="row align-items-center">
                    <div class="col">
                      <div class="text-xs fw-bold text-uppercase mb-1">
                        Pending
                      </div>
                      <div class="h5 mb-0 fw-bold text-warning">
                        {{ pendingCount }}
                      </div>
                    </div>
                    <div class="col-auto">
                      <i class="bi bi-clock-history fa-2x text-gray-300"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-xl-3 col-md-6">
              <div class="card stats-card rejected h-100">
                <div class="card-body">
                  <div class="row align-items-center">
                    <div class="col">
                      <div class="text-xs fw-bold text-uppercase mb-1">
                        Rejected
                      </div>
                      <div class="h5 mb-0 fw-bold text-danger">
                        {{ rejectedCount }}
                      </div>
                    </div>
                    <div class="col-auto">
                      <i class="bi bi-x-circle fa-2x text-gray-300"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Filters -->
          <div class="card mb-4">
            <div class="card-body filter-section">
              <div class="row align-items-center">
                <!-- Employee (single select) -->
                <div class="col-md-3 mb-2">
                  <label class="form-label">Employee</label>
                  <select class="form-select" v-model="carerFilterSingle">
                    <option value="all">All employees</option>
                    <option
                      v-for="carer in carersList"
                      :key="carer.value"
                      :value="carer.value"
                    >
                      {{ carer.label }}
                    </option>
                  </select>
                </div>
                <div class="col-md-3 mb-2">
                  <label class="form-label">Status</label>
                  <select class="form-select" v-model="statusFilter">
                    <option value="all">All Statuses</option>
                    <option value="approved">Approved</option>
                    <option value="pending">Pending</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                <div class="col-md-3 mb-2">
                  <label class="form-label">Date Range</label>
                  <select class="form-select" v-model="dateFilter">
                    <option value="all">All Dates</option>
                    <option value="today">Today</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                    <option value="range">Custom range</option>
                  </select>
                </div>
                <!-- From / To (visible only for custom range) -->
                <div class="col-md-3 mb-2" v-if="dateFilter === 'range'">
                  <label class="form-label">From</label>
                  <input type="date" class="form-control" v-model="startDate" />
                </div>
                <div class="col-md-3 mb-2" v-if="dateFilter === 'range'">
                  <label class="form-label">To</label>
                  <input type="date" class="form-control" v-model="endDate" />
                </div>
                <div class="col-md-3 mb-2">
                  <label class="form-label">Office Location</label>
                  <select class="form-select" v-model="careHomeFilter">
                    <option value="all">All Office Locations</option>
                    <option v-for="home in careHomes" :key="home" :value="home">
                      {{ home }}
                    </option>
                  </select>
                </div>
                <div class="col-md-3 mb-2 d-flex align-items-end">
                  <button class="btn btn-secondary w-100" @click="clearFilters">
                    <i class="bi bi-arrow-repeat me-1"></i> Reset Filters
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Timesheets Table -->
          <div class="card">
            <div
              class="card-header d-flex justify-content-between align-items-center"
            >
              <h6 class="m-0">Timesheet Entries</h6>
              <div>
                <button
                  class="btn btn-sm btn-outline-secondary me-2"
                  @click="exportToPDF"
                >
                  <i class="bi bi-download me-1"></i> Export
                </button>
                <button class="btn btn-sm btn-primary" @click="refetch">
                  <i class="bi bi-arrow-repeat me-1"></i> Refresh
                </button>
              </div>
            </div>
            <div class="card-body">
              <div class="table-responsive">
                <table class="table table-hover">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Office Location</th>
                      <th>Clock In</th>
                      <th>Clock Out</th>
                      <th>Hours</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="entry in filteredClockInOuts" :key="entry.id">
                      <td>
                        <div class="d-flex align-items-center">
                          <img
                            :src="getUserAvatar(entry.users_permissions_user)"
                            class="user-avatar me-2"
                          />
                          <div>
                            <div class="fw-bold">
                              {{ getUserName(entry.users_permissions_user) }}
                            </div>
                            <small class="text-muted">{{
                              getUserPosition(entry.users_permissions_user)
                            }}</small>
                          </div>
                        </div>
                      </td>
                      <td>{{ getCareHomeName(entry.carehome) }}</td>
                      <td>
                        <div>{{ formatDate(entry.in) }}</div>
                        <small class="text-muted">{{
                          formatTime(entry.in)
                        }}</small>

                        <div
                          v-if="entry.locationin"
                          class="small"
                          :class="[
                            formatLocation(
                              entry.locationin,
                              entry.carehome.gps
                            ) > 0.2
                              ? 'text-danger'
                              : 'text-success',
                          ]"
                        >
                          {{
                            formatLocation(
                              entry.locationin,
                              entry.carehome.gps
                            )
                          }}km
                        </div>
                      </td>
                      <td>
                        <div v-if="entry.out">{{ formatDate(entry.out) }}</div>
                        <div v-else class="text-muted">Not clocked out</div>
                        <small v-if="entry.out" class="text-muted">{{
                          formatTime(entry.out)
                        }}</small>
                        <div
                          v-if="entry.out && entry.locationout"
                          class="small"
                          :class="[
                            formatLocation(
                              entry.locationout,
                              entry.carehome.gps
                            ) > 0.2
                              ? 'text-danger'
                              : 'text-success',
                          ]"
                        >
                          {{
                            formatLocation(
                              entry.locationout,
                              entry.carehome.gps
                            )
                          }}km
                        </div>
                      </td>
                      <td>
                        <span class="fw-bold">{{
                          calculateHours(entry.in, entry.out)
                        }}</span>
                        hrs
                      </td>
                      <td>
                        <span
                          class="badge"
                          :class="{
                            'bg-success text-white':
                              entry.decision === 'approved',
                            'bg-danger text-white':
                              entry.decision === 'rejected',
                            'bg-warning text-dark':
                              !entry.decision || entry.decision === 'pending',
                          }"
                        >
                          {{ entry.decision || "pending" }}
                        </span>
                      </td>
                      <td>
                        <div class="btn-group" v-if="entry.out">
                          <button
                            v-if="
                              !entry.decision || entry.decision === 'rejected'
                            "
                            class="btn btn-sm btn-success action-btn"
                            @click="
                              updateDecision(entry.documentId, 'approved')
                            "
                          >
                            <i class="bi bi-check-lg"></i> Approve
                          </button>
                          <button
                            v-if="
                              !entry.decision || entry.decision === 'approved'
                            "
                            class="btn btn-sm btn-danger action-btn"
                            @click="
                              updateDecision(entry.documentId, 'rejected')
                            "
                          >
                            <i class="bi bi-x-lg"></i> Reject
                          </button>

                          <!-- Option with chat icon -->
                          <button
                            class="btn btn-sm btn-outline-info action-btn"
                            @click="openAddNoteModal(entry)"
                            :title="
                              hasNote(entry) ? 'View/Edit Note' : 'Add Note'
                            "
                            style="cursor: pointer; font-size: 1.2rem"
                          >
                            <i
                              class="bi bi-chat-left-text"
                              :class="{
                                'text-info': !hasNote(entry),
                                'text-warning': hasNote(entry),
                              }"
                            ></i>
                          </button>
                        </div>

                        <!-- Bootstrap Vue Modal for Adding Notes -->
                        <b-modal
                          v-model="noteModal"
                          id="note-modal"
                          ref="note-modal"
                          title="Add Note to Timesheet"
                          @hidden="resetNoteModal"
                          @ok="saveNote"
                        >
                          <textarea
                            v-model="noteText"
                            class="form-control"
                            rows="4"
                            placeholder="Enter your note here..."
                          ></textarea>

                          <template #modal-footer="{ ok, cancel }">
                            <b-button
                              size="sm"
                              variant="secondary"
                              @click="cancel()"
                            >
                              Cancel
                            </b-button>
                            <b-button size="sm" variant="primary" @click="ok()">
                              Save Note
                            </b-button>
                          </template>
                        </b-modal>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Empty State -->
              <div
                v-if="filteredClockInOuts.length === 0"
                class="text-center py-5"
              >
                <i class="bi bi-inbox display-4 text-muted"></i>
                <p class="mt-3 text-muted">No timesheet entries found</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from "vue";
import {
  GET_CLOCKINOUTS,
  UPDATE_CLOCKINOUTDECISION,
  UPDATE_CLOCKINOUTNOTE,
} from "@/hooks/queries";
import { useQuery, useMutation } from "@vue/apollo-composable";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default {
  name: "TimeSheets",
  setup() {
        const userString = localStorage.getItem('user');
        const user = userString ? JSON.parse(userString) : null;
        const adminid = user?.id || '';

    // Use Apollo useQuery hook
    const { result, loading, error, refetch } = useQuery(GET_CLOCKINOUTS, {admin: String(adminid)});
    const { mutate: updateClockinoutDecision } = useMutation(
      UPDATE_CLOCKINOUTDECISION
    );
    const { mutate: updateClockinoutNote } = useMutation(UPDATE_CLOCKINOUTNOTE);

    // Local state
    const searchQuery = ref("");
    const statusFilter = ref("all");
    const dateFilter = ref("all");
    const careHomeFilter = ref("all");

    const noteText = ref("");
    const selectedEntryForNote = ref(null);
    const noteModal = ref(false);

    const startDate = ref(""); // "YYYY-MM-DD"
    const endDate = ref("");
    const carerFilterSingle = ref("all");

    let km = 0;

    const carersList = computed(() => {
      const map = new Map();
      clockInOuts.value.forEach((entry) => {
        const u = entry.users_permissions_user;
        if (u?.documentId) {
          const label = getUserName(u) || u.username || u.email || u.documentId;
          map.set(u.documentId, label);
        }
      });
      return Array.from(map, ([value, label]) => ({ value, label })).sort(
        (a, b) => a.label.localeCompare(b.label)
      );
    });

    // (optional) simple validity check for UX (disable Export if invalid)
    const isDateRangeValid = computed(() => {
      if (dateFilter.value !== "range") return true;
      if (!startDate.value || !endDate.value) return false;
      return new Date(startDate.value) <= new Date(endDate.value);
    });

    // Haversine formula
    const getDistanceFromLatLonInKm = (lat1, lon1, lat2, lon2) => {
      const R = 6371; // Radius of the earth in km
      const dLat = deg2rad(lat2 - lat1);
      const dLon = deg2rad(lon2 - lon1);
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(deg2rad(lat1)) *
          Math.cos(deg2rad(lat2)) *
          Math.sin(dLon / 2) *
          Math.sin(dLon / 2);

      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const distance = R * c; // Distance in km
      return distance;
    };

    const deg2rad = (deg) => {
      return deg * (Math.PI / 180);
    };

    const hasNote = (entry) => {
      return entry.note?.length > 0;
    };

    const openAddNoteModal = (entry) => {
      selectedEntryForNote.value = entry;
      noteText.value = entry.note;

      noteModal.value = true;
    };

    const saveNote = async () => {
      if (selectedEntryForNote.value && noteText.value.trim()) {
        // Save note to backend

        const variables = {
          data: {
            note: noteText.value,
          },
        };

        await updateClockinoutNote({
          id: selectedEntryForNote.value.documentId,
          data: variables.data,
        });

        refetch();
      }
    };

    const resetNoteModal = () => {
      noteText.value = "";
      selectedEntryForNote.value = null;
    };

    // Extract clockInOuts from Apollo result
    const clockInOuts = computed(() => {
      return result.value?.clockinouts || [];
    });

    const careHomes = computed(() => {
      const homes = new Set();
      clockInOuts.value.forEach((entry) => {
        if (entry.carehome?.streetname) {
          homes.add(entry.carehome.streetname);
        }
      });
      return Array.from(homes);
    });

    // Update decision mutation would be here
    const updateDecision = async (id, decision) => {
      const variables = {
        data: {
          decision: decision,
        },
      };

      await updateClockinoutDecision({
        id: id,
        data: variables.data,
      });

      refetch();
    };

    // Helper methods
    const getUserName = (userData) => {
      if (!userData) return "Unknown User";
      const { fullname } = userData;
      return fullname;
    };

    const getUserPosition = (userData) => {
      return userData?.position || "";
    };

    const getUserAvatar = (userData) => {
      return (
        userData?.avatar || "https://randomuser.me/api/portraits/lego/1.jpg"
      );
    };

    const getCareHomeName = (carehomeData) => {
      return carehomeData?.streetname || "Unknown Office Location";
    };

    const formatLocation = (location, carehomegps) => {
      if (!location) return "";
      try {
        const loc =
          typeof location === "string" ? JSON.parse(location) : location;
        //console.log(carehomegps.lat+" "+carehomegps.lng+" "+location.latitude+" "+location.longitude);
        km = getDistanceFromLatLonInKm(
          loc.latitude,
          loc.longitude,
          carehomegps.lat,
          carehomegps.lng
        );
        //console.log(km);
        //return `Lat: ${loc.latitude?.toFixed(4)}, Lng: ${loc.longitude?.toFixed(4)} `+km.toFixed(2)+"km";
        return parseFloat(km.toFixed(2));
      } catch {
        return "Location data";
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    };

    const formatTime = (dateString) => {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    const calculateHours = (clockIn, clockOut) => {
      if (!clockIn) return 0;
      const start = new Date(clockIn);
      const end = clockOut ? new Date(clockOut) : new Date();
      const hours = (end - start) / (1000 * 60 * 60);
      return hours.toFixed(2);
    };

    const clearFilters = () => {
      searchQuery.value = "";
      statusFilter.value = "all";
      dateFilter.value = "all";
      careHomeFilter.value = "all";
      startDate.value = "";
      endDate.value = "";
      carerFilterSingle.value = "";
    };

    // Computed properties
    const approvedCount = computed(
      () =>
        clockInOuts.value.filter((entry) => entry.decision === "approved")
          .length
    );

    const pendingCount = computed(
      () =>
        clockInOuts.value.filter(
          (entry) => !entry.decision || entry.decision === "pending"
        ).length
    );

    const rejectedCount = computed(
      () =>
        clockInOuts.value.filter((entry) => entry.decision === "rejected")
          .length
    );

    const filteredClockInOuts = computed(() => {
      if (!clockInOuts.value.length) return [];

      const today = new Date();
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - today.getDay());
      weekStart.setHours(0, 0, 0, 0);

      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

      return clockInOuts.value.filter((entry) => {
        const user = entry.users_permissions_user;
        const carehome = entry.carehome;

        const userName = user
          ? `${user.fullname || ""} ${user.lastname || ""}`.trim() ||
            user.username
          : "";
        const carehomeName = carehome?.streetname || "";

        const entryDate = new Date(entry.in);

        const matchesSearch =
          searchQuery.value === "" ||
          userName.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
          carehomeName.toLowerCase().includes(searchQuery.value.toLowerCase());

        const matchesStatus =
          statusFilter.value === "all" ||
          (statusFilter.value === "pending" &&
            (!entry.decision || entry.decision === "pending")) ||
          entry.decision === statusFilter.value;

        const matchesCareHome =
          careHomeFilter.value === "all" ||
          carehomeName === careHomeFilter.value;

        const matchesCarer =
          carerFilterSingle.value === "all" ||
           user?.documentId === carerFilterSingle.value;
      
        // Date filter
        const matchesDate = (() => {
          if (dateFilter.value === "all") return true;

          if (dateFilter.value === "today") {
            return entryDate.toDateString() === today.toDateString();
          }

          if (dateFilter.value === "week") {
            return entryDate >= weekStart;
          }

          if (dateFilter.value === "month") {
            return entryDate >= monthStart;
          }

          if (dateFilter.value === "range") {
            // if either date missing, treat as open-ended
            let from = startDate.value
              ? new Date(`${startDate.value}T00:00:00`)
              : new Date(-8640000000000000); // min date

            let to = endDate.value
              ? new Date(`${endDate.value}T23:59:59.999`)
              : new Date(8640000000000000); // max date

            return entryDate >= from && entryDate <= to;
          }

          return true;
        })();

        return (
          matchesSearch &&
          matchesStatus &&
          matchesCareHome &&
          matchesDate &&
          matchesCarer
        );
      });
    });

    const carersLookup = computed(() =>
      Object.fromEntries(carersList.value.map((c) => [c.value, c.label]))
    );

    
    const exportToPDF = () => {
      // Build rows from whatever is *currently displayed*
      const rows = filteredClockInOuts.value.map((entry) => {
        const carer = getUserName(entry.users_permissions_user);
        const carehome = getCareHomeName(entry.carehome);
        const inDate = entry.in
          ? `${formatDate(entry.in)} ${formatTime(entry.in)}`
          : "N/A";
        const outDate = entry.out
          ? `${formatDate(entry.out)} ${formatTime(entry.out)}`
          : "N/A";
        const hours = calculateHours(entry.in, entry.out);
        const status = entry.decision || "pending";

        return [carer, carehome, inDate, outDate, hours, status];
      });

      const doc = new jsPDF({ orientation: "landscape", unit: "pt" }); // wide table fits better
      const now = new Date();
      const printedOn = now.toLocaleString();

      const carersLabel =
      carerFilterSingle.value !== "all"
        ? `Carer: ${carersLookup.value[carerFilterSingle.value]}`
        : null;

      // Title / header
      doc.setFontSize(16);
      doc.text("Timesheets", 40, 40);
      doc.setFontSize(10);
      doc.text(`Printed on: ${printedOn}`, 40, 60);

      // Optional: show active filters in subtitle
      const filtersLine = [
        statusFilter.value !== "all" ? `Status: ${statusFilter.value}` : null,
        dateFilter.value === "range" && startDate.value && endDate.value
          ? `Date: ${startDate.value} to ${endDate.value}`
          : dateFilter.value !== "all"
          ? `Date: ${dateFilter.value}`
          : null,
        careHomeFilter.value !== "all"
          ? `Care Home: ${careHomeFilter.value}`
          : null,
        searchQuery.value ? `Search: "${searchQuery.value}"` : null,
        carersLabel,
      ]
        .filter(Boolean)
        .join("   |   ");

      if (filtersLine) doc.text(filtersLine, 40, 78);

      autoTable(doc, {
        startY: 100,
        head: [
          ["Employee", "Office Location", "Clock In", "Clock Out", "Hours", "Status"],
        ],
        body: rows,
        styles: { fontSize: 9, cellPadding: 6, overflow: "linebreak" },
        headStyles: { fillColor: [230, 230, 230] },
        didDrawPage: () => {
          // Footer with page numbers
          const pageSize = doc.internal.pageSize;
          const pageWidth = pageSize.getWidth();
          const pageHeight = pageSize.getHeight();
          const page = doc.getNumberOfPages();
          doc.setFontSize(9);
          doc.text(`Page ${page}`, pageWidth - 60, pageHeight - 20);
        },
      });

      const fileName = `Timesheets_${now.toISOString().slice(0, 10)}.pdf`;
      doc.save(fileName);
    };

    return {
      exportToPDF,
      km,
      hasNote,
      getDistanceFromLatLonInKm,
      deg2rad,
      // Modal
      noteText,
      noteModal,
      openAddNoteModal,
      saveNote,
      resetNoteModal,

      // Apollo
      result,
      loading,
      error,
      refetch,

      // Local state
      clockInOuts,
      searchQuery,
      statusFilter,
      dateFilter,
      careHomeFilter,
      careHomes,

      // Computed
      approvedCount,
      pendingCount,
      rejectedCount,
      filteredClockInOuts,

      // Methods
      updateDecision,
      getUserName,
      getUserPosition,
      getUserAvatar,
      getCareHomeName,
      formatLocation,
      formatDate,
      formatTime,
      calculateHours,
      clearFilters,
      startDate,
      endDate,
      isDateRangeValid,
      carersList,
      carerFilterSingle,
    };
  },
};
</script>

<style scoped>
:root {
  --primary: #4e73df;
  --success: #1cc88a;
  --warning: #f6c23e;
  --danger: #e74a3b;
  --secondary: #858796;
  --light: #f8f9fc;
  --dark: #5a5c69;
}

.container-fluid {
  max-width: 80%;
}

body {
  background-color: #f8f9fc;
  font-family: "Nunito", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    sans-serif;
}

.sidebar {
  min-height: 100vh;
  background: linear-gradient(180deg, var(--primary) 10%, #224abe 100%);
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
}

.sidebar .nav-item {
  margin-bottom: 0.25rem;
}

.sidebar .nav-link {
  color: rgba(255, 255, 255, 0.8);
  padding: 1rem;
}

.sidebar .nav-link:hover {
  color: #fff;
}

.sidebar .nav-link.active {
  font-weight: bold;
  color: #fff;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 0.35rem;
}

.topbar {
  height: 4.375rem;
  box-shadow: 0 0.15rem 1.75rem 0 rgba(58, 59, 69, 0.15);
}

.card {
  border: none;
  border-radius: 0.35rem;
  box-shadow: 0 0.15rem 1.75rem 0 rgba(58, 59, 69, 0.1);
}

.card .card-header {
  background-color: #f8f9fc;
  border-bottom: 1px solid #e3e6f0;
  font-weight: bold;
}

.badge-approved {
  background-color: var(--success);
  color: white;
}

.badge-pending {
  background-color: var(--warning);
  color: white;
}

.badge-rejected {
  background-color: var(--danger);
  color: white;
}

.stats-card {
  border-left: 4px solid;
}

.stats-card.total {
  border-left-color: var(--primary);
}

.stats-card.approved {
  border-left-color: var(--success);
}

.stats-card.pending {
  border-left-color: var(--warning);
}

.stats-card.rejected {
  border-left-color: var(--danger);
}

.table th {
  border-top: none;
  font-weight: 600;
  color: var(--dark);
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}

.action-btn {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.filter-section {
  background-color: #f8f9fc;
  border-radius: 0.35rem;
  padding: 1rem;
  margin-bottom: 1.5rem;
}

@media (max-width: 768px) {
  .sidebar {
    min-height: auto;
  }

  .stats-card {
    margin-bottom: 1rem;
  }
}
</style>
